import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import { useMyGreenhouseTabContext } from 'talent_matching/contexts/my_greenhouse_tab_context';
import type { MatchNarrative } from 'talent_matching/types';

const narrativeKey = (candidateId: number | string) =>
  ['forager-narrative', candidateId] as const;

export const useMatchNarrative = (candidateId: number | string) => {
  const { paths } = useMyGreenhouseTabContext();
  const queryClient = useQueryClient();
  const path = paths.matchNarrativePath;

  const { data: narrative } = useQuery<MatchNarrative | null>({
    queryKey: narrativeKey(candidateId),
    queryFn: () =>
      queryClient.getQueryData<MatchNarrative>(narrativeKey(candidateId)) ??
      null,
    enabled: false,
    staleTime: Infinity,
  });

  const mutation = useMutation({
    mutationFn: async (): Promise<MatchNarrative> => {
      const { data } = await standardRequestWithErrorFlash.post<MatchNarrative>(
        path as string,
        { profile_id: candidateId }
      );
      return data;
    },
    onSuccess: (result) => {
      queryClient.setQueryData(narrativeKey(candidateId), result);
    },
  });

  return {
    enabled: Boolean(path),
    narrative: narrative ?? undefined,
    request: () => mutation.mutate(),
    isLoading: mutation.isPending,
  };
};
